import {
	type CurrencyEnum,
	OrderAdjustmentCalculationType,
	OrderAdjustmentType,
} from '@codezo/graphql';
import { Plus, X } from 'lucide-react';
import { Input } from '../../../ui/input';
import { MarginInput } from '../../../ui/margin-input';
import { Textarea } from '../../../ui/textarea';
import { cn } from '../../../../lib/utils';
import type { AdjustmentInput } from './types';

interface AdjustmentsTabProps {
	activeItemId: string;
	adjustments: Record<string, AdjustmentInput[]>;
	addAdjustment: () => void;
	updateAdjustment: (idx: number, patch: Partial<AdjustmentInput>) => void;
	removeAdjustment: (idx: number) => void;
	currency: CurrencyEnum;
}

export function AdjustmentsTab({
	activeItemId,
	adjustments,
	addAdjustment,
	updateAdjustment,
	removeAdjustment,
	currency,
}: AdjustmentsTabProps) {
	const itemAdjustments = adjustments[activeItemId] ?? [];

	return (
		<div className="flex flex-col gap-3">
			{itemAdjustments.length === 0 ? (
				<div className="flex flex-col items-center gap-3 py-12 text-center">
					<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
						<Plus size={18} className="text-text-4" />
					</div>
					<div className="text-sm text-text-3">No adjustments</div>
					<div className="text-xs text-text-4">Add fees or discounts to this offer</div>
					<button
						type="button"
						onClick={addAdjustment}
						className="mt-1 flex items-center gap-1.5 rounded-lg border border-line-2 px-3 py-1.5 text-sm text-text-2 transition-colors hover:border-line-3 hover:text-text-1"
					>
						<Plus size={12} />
						Add adjustment
					</button>
				</div>
			) : (
				<>
					<div className="flex justify-end">
						<button
							type="button"
							onClick={addAdjustment}
							className="flex items-center gap-1.5 rounded-md bg-surface-input px-2.5 py-1 text-xs text-text-2 transition-colors hover:text-text-1"
						>
							<Plus size={11} />
							Add
						</button>
					</div>
					<div className="flex flex-col gap-2">
						{itemAdjustments.map((adj, idx) => (
							<div key={idx} className="rounded-lg border border-line-1 p-3">
								<div className="mb-2.5 flex items-center gap-2">
									<div className="flex gap-1">
										<button
											type="button"
											onClick={() =>
												updateAdjustment(idx, {
													type: OrderAdjustmentType.Fee,
												})
											}
											className={cn(
												'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
												adj.type === OrderAdjustmentType.Fee
													? 'border-status-warn bg-status-warn/10 text-status-warn'
													: 'border-line-2 text-text-4 hover:border-line-3'
											)}
										>
											Fee
										</button>
										<button
											type="button"
											onClick={() =>
												updateAdjustment(idx, {
													type: OrderAdjustmentType.Discount,
												})
											}
											className={cn(
												'rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
												adj.type === OrderAdjustmentType.Discount
													? 'border-status-success bg-status-success/10 text-status-success'
													: 'border-line-2 text-text-4 hover:border-line-3'
											)}
										>
											Discount
										</button>
									</div>
									<div className="min-w-0 flex-1" />
									<button
										type="button"
										onClick={() => removeAdjustment(idx)}
										className="rounded p-1 text-text-4 transition-colors hover:bg-surface-input hover:text-status-danger"
									>
										<X size={12} />
									</button>
								</div>
								<div className="grid grid-cols-2 gap-2">
									<div className="flex flex-col gap-1">
										<span className="text-2xs text-text-4">Name</span>
										<Input
											value={adj.name}
											onChange={(e) =>
												updateAdjustment(idx, { name: e.target.value })
											}
											placeholder="e.g. Setup fee"
										/>
									</div>
									<div className="flex flex-col gap-1">
										<span className="text-2xs text-text-4">Value</span>
										<MarginInput
											value={adj.value ? String(adj.value) : ''}
											onChange={(v) =>
												updateAdjustment(idx, {
													value: v ? parseFloat(v) : 0,
												})
											}
											mode={
												adj.calculationType ===
												OrderAdjustmentCalculationType.Percentage
													? 'percentage'
													: 'fixed'
											}
											onModeChange={(m) =>
												updateAdjustment(idx, {
													calculationType:
														m === 'percentage'
															? OrderAdjustmentCalculationType.Percentage
															: OrderAdjustmentCalculationType.Fixed,
													currency: m === 'fixed' ? currency : undefined,
												})
											}
											currency={currency}
										/>
									</div>
								</div>
								<div className="mt-2 flex flex-col gap-1">
									<span className="text-2xs text-text-4">Description</span>
									<Textarea
										value={adj.description ?? ''}
										onChange={(e) =>
											updateAdjustment(idx, {
												description: e.target.value || undefined,
											})
										}
										placeholder="Optional"
										className="min-h-15"
										rows={2}
									/>
								</div>
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
}
